import React, { useState } from 'react'
import { Calendar, Video, User, Building, MessageSquare, Clock, CheckCircle, XCircle, Link as LinkIcon, AlertCircle, RefreshCw, X, Search, Filter, ChevronDown, SortAsc, SortDesc } from 'lucide-react'

const CompanyInterviews = () => {
  const [activeTab, setActiveTab] = useState('upcoming')

  const interviews = [
    // Currently (Live/Today)
    {
      id: 1,
      candidateName: "Saikiran",
      adminName: "Kamni",
      time: "Today, 5:00 PM",
      status: "currently",
      meetingLink: "https://meet.google.com/abc-123",
      role: "Junior Java Dev",
      experience: 2,
      technologies: ["Java", "Spring Boot"],
      skills: ["Problem Solving"]
    },
    // Upcoming
    {
      id: 2,
      candidateName: "Dinesh",
      adminName: "Rukmini",
      time: "Tomorrow, 10:00 AM",
      status: "upcoming",
      meetingLink: "https://meet.google.com/xyz-789",
      role: "Senior React Developer",
      experience: 5,
      technologies: ["React", "Redux", "Node.js"],
      skills: ["Architecture", "Team Leadership"]
    },
    {
      id: 3,
      candidateName: "Vardhan",
      adminName: "Satwika",
      time: "Jan 25, 2:00 PM",
      status: "upcoming",
      meetingLink: "https://meet.google.com/def-456",
      role: "UX Designer",
      experience: 4,
      technologies: ["Figma", "Adobe XD"],
      skills: ["Prototyping", "User Research"]
    },
    {
      id: 4,
      candidateName: "Ramoji",
      adminName: "Alina",
      time: "Jan 20, 11:30 AM",
      status: "upcoming",
      meetingLink: "https://meet.google.com/ghi-789",
      role: "Python Dev",
      experience: 3,
      technologies: ["Python", "Django"],
      skills: ["API Design"]
    },
    {
      id: 5,
      candidateName: "Navaneeth",
      adminName: "Alina",
      time: "Jan 18, 4:00 PM",
      status: "upcoming",
      meetingLink: "https://meet.google.com/jkl-012",
      role: "Frontend developer",
      experience: 3,
      technologies: ["JavaScript", "Vue.js"],
      skills: ["Frontend Architecture"]
    },
    // Completed
    {
      id: 6,
      candidateName: "Akhil",
      adminName: "Rukmini",
      companyName: "AJA Consulting",
      time: "Jan 10, 1:00 PM",
      status: "completed",
      outcome: "rejected",
      feedback: "Technical skills were good, but cultural fit was not aligned with the team's values.",
      role: "DevOps Engineer",
      experience: 6,
      technologies: ["AWS", "Docker", "Kubernetes"],
      skills: ["CI/CD"]
    },
    {
      id: 7,
      candidateName: "Arjun",
      adminName: "Dhiraj",
      companyName: "AJA Consulting",
      time: "Jan 08, 11:00 AM",
      status: "completed",
      outcome: "pipeline", // Succeed
      feedback: "Excellent problem-solving skills and communication. Proceeding to the final round.",
      role: "Frontend Developer",
      experience: 4,
      technologies: ["React", "TypeScript"],
      skills: ["Communication"]
    },
    {
      id: 8,
      candidateName: "Maya",
      adminName: "Kamni",
      companyName: "AJA Consulting",
      time: "Jan 05, 3:30 PM",
      status: "completed",
      outcome: "pipeline",
      feedback: "Strong candidate. Scheduled for system design round.",
      role: "Dot net Dev",
      experience: 5,
      technologies: [".NET", "C#"],
      skills: ["System Design"]
    },
    {
      id: 9,
      candidateName: "Soumya",
      adminName: "Shiresha",
      companyName: "AJA Consulting",
      time: "Jan 03, 10:00 AM",
      status: "completed",
      outcome: "rejected",
      feedback: "Lacked experience with required cloud technologies.",
      role: "Cloud Architect",
      experience: 8,
      technologies: ["Azure", "Terraform"],
      skills: ["Cloud Security"]
    },
    {
      id: 10,
      candidateName: "Arun",
      adminName: "Sravya",
      companyName: "AJA Consulting",
      time: "Dec 28, 2:00 PM",
      status: "completed",
      outcome: "pipeline",
      feedback: "Passed technical assessment with flying colors.",
      role: "QA Engineer",
      experience: 3,
      technologies: ["Selenium", "Java"],
      skills: ["Automation Testing"]
    }
  ]

  const [filters, setFilters] = useState({
    role: '',
    experience: '',
    technology: '',
    skill: '',
    adminName: '',
    resourceName: ''
  })
  const [sortOrder, setSortOrder] = useState('asc') // 'asc' or 'desc' for Date

  // Unique options for select inputs
  const roles = [...new Set(interviews.map(i => i.role))]
  const experiences = [...new Set(interviews.map(i => i.experience))].sort((a, b) => a - b)
  const technologies = [...new Set(interviews.flatMap(i => i.technologies || []))].sort()
  const skills = [...new Set(interviews.flatMap(i => i.skills || []))].sort()
  const admins = [...new Set(interviews.map(i => i.adminName))].sort()

  const filteredInterviews = interviews
    .filter(i => {
      const matchStatus = i.status === activeTab
      const matchRole = !filters.role || i.role === filters.role
      const matchExp = !filters.experience || i.experience === Number(filters.experience)
      const matchTech = !filters.technology || (i.technologies && i.technologies.includes(filters.technology))
      const matchSkill = !filters.skill || (i.skills && i.skills.includes(filters.skill))
      const matchAdmin = !filters.adminName || i.adminName === filters.adminName
      const matchResource = !filters.resourceName || i.candidateName.toLowerCase().includes(filters.resourceName.toLowerCase())

      return matchStatus && matchRole && matchExp && matchTech && matchSkill && matchAdmin && matchResource
    })
    .sort((a, b) => {
      // Simple string comparison for now as dates are not ISO. 
      // In a real app, parse `a.time` and `b.time` to Date objects. 
      if (sortOrder === 'asc') return a.time.localeCompare(b.time)
      return b.time.localeCompare(a.time)
    })

  const stats = {
    total: interviews.length,
    upcoming: interviews.filter(i => i.status === 'upcoming' || i.status === 'currently').length,
    completed: interviews.filter(i => i.status === 'completed').length,
    rejected: interviews.filter(i => i.status === 'completed' && i.outcome === 'rejected').length
  }


  const TabButton = ({ name, label, count }) => (
    <button
      onClick={() => setActiveTab(name)}
      className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${activeTab === name
        ? 'border-primary-600 text-primary-600'
        : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
        }`}
    >
      {label}
      <span className={`px-2 py-0.5 rounded-full text-xs ${activeTab === name ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600'
        }`}>
        {count}
      </span>
    </button>
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Calendar className="w-6 h-6 text-primary-600 mr-2" />
          <h1 className="text-2xl font-bold text-secondary-900">Interviews</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center gap-4 transition-all hover:shadow-md">
          <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
            <Calendar className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm text-gray-500 font-medium">Total Interviews</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center gap-4 transition-all hover:shadow-md">
          <div className="p-3 bg-yellow-50 text-yellow-600 rounded-lg">
            <Clock className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm text-gray-500 font-medium">Upcoming</p>
            <p className="text-2xl font-bold text-gray-900">{stats.upcoming}</p>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center gap-4 transition-all hover:shadow-md">
          <div className="p-3 bg-green-50 text-green-600 rounded-lg">
            <CheckCircle className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm text-gray-500 font-medium">Completed</p>
            <p className="text-2xl font-bold text-gray-900">{stats.completed}</p>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center gap-4 transition-all hover:shadow-md">
          <div className="p-3 bg-red-50 text-red-600 rounded-lg">
            <XCircle className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm text-gray-500 font-medium">Rejected</p>
            <p className="text-2xl font-bold text-gray-900">{stats.rejected}</p>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <Filter className="w-4 h-4 text-primary-600" />
          <h3 className="text-sm font-semibold text-gray-700">Filters & Sorting</h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {/* Resource Name Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search Resource Name"
              value={filters.resourceName}
              onChange={(e) => setFilters(prev => ({ ...prev, resourceName: e.target.value }))}
              className="w-full pl-9 pr-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          </div>

          {/* Role Filter */}
          <div className="relative">
            <select
              value={filters.role}
              onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
              className="w-full pl-9 pr-3 py-2 bg-white border border-gray-300 rounded-lg text-sm appearance-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Roles</option>
              {roles.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <User className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>

          {/* Experience Filter */}
          <div className="relative">
            <select
              value={filters.experience}
              onChange={(e) => setFilters(prev => ({ ...prev, experience: e.target.value }))}
              className="w-full pl-9 pr-3 py-2 bg-white border border-gray-300 rounded-lg text-sm appearance-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Experience</option>
              {experiences.map(e => <option key={e} value={e}>{e} Years</option>)}
            </select>
            <Clock className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>

          {/* Admin Name Filter */}
          <div className="relative">
            <select
              value={filters.adminName}
              onChange={(e) => setFilters(prev => ({ ...prev, adminName: e.target.value }))}
              className="w-full pl-9 pr-3 py-2 bg-white border border-gray-300 rounded-lg text-sm appearance-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Admins</option>
              {admins.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <User className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>

          {/* Technology Filter */}
          <div className="relative">
            <select
              value={filters.technology}
              onChange={(e) => setFilters(prev => ({ ...prev, technology: e.target.value }))}
              className="w-full pl-9 pr-3 py-2 bg-white border border-gray-300 rounded-lg text-sm appearance-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Technologies</option>
              {technologies.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <Filter className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>

          {/* Skill Filter */}
          <div className="relative">
            <select
              value={filters.skill}
              onChange={(e) => setFilters(prev => ({ ...prev, skill: e.target.value }))}
              className="w-full pl-9 pr-3 py-2 bg-white border border-gray-300 rounded-lg text-sm appearance-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Skills</option>
              {skills.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <Filter className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>

          {/* Date Sort */}
          <button
            onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
            className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            {sortOrder === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />}
            Sort by Date: {sortOrder === 'asc' ? 'Oldest First' : 'Newest First'}
          </button>

          {/* Clear Filters */}
          <button
            onClick={() => {
              setFilters({
                role: '',
                experience: '',
                technology: '',
                skill: '',
                adminName: '',
                resourceName: ''
              })
              setSortOrder('asc')
            }}
            className="flex items-center justify-center gap-2 w-full px-4 py-2 text-red-600 bg-red-50 border border-red-100 rounded-lg text-sm hover:bg-red-100 transition-colors"
          >
            <X className="w-4 h-4" />
            Clear Filters
          </button>
        </div>
      </div>

      <div className="border-b border-gray-200">
        <div className="flex space-x-8">
          <TabButton name="currently" label="Currently" count={interviews.filter(i => i.status === 'currently').length} />
          <TabButton name="upcoming" label="Upcoming" count={interviews.filter(i => i.status === 'upcoming').length} />
          <TabButton name="completed" label="Completed" count={interviews.filter(i => i.status === 'completed').length} />
        </div>
      </div>

      <div className="space-y-4">
        {filteredInterviews.length > 0 ? (
          filteredInterviews.map((interview) => (
            <div key={interview.id} className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm hover:border-primary-200 transition-colors">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">

                {/* Left Side: Basic Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-secondary-900">{interview.candidateName}</h3>
                    <span className="text-sm px-2 py-0.5 bg-blue-50 text-blue-700 rounded border border-blue-100 font-medium">
                      {interview.role}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 text-sm text-secondary-600">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-secondary-400" />
                      <span>Admin: <span className="font-medium text-secondary-900">{interview.adminName}</span></span>
                    </div>

                    {(activeTab === 'currently' || activeTab === 'upcoming') && (
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-secondary-400" />
                        <span className="font-medium text-secondary-900">{interview.time}</span>
                      </div>
                    )}

                    {activeTab === 'completed' && (
                      <div className="flex items-center gap-2">
                        <Building className="w-4 h-4 text-secondary-400" />
                        <span>{interview.companyName}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Side / Status Specifics */}
                <div className="flex-1 md:flex-none md:w-5/12 border-t md:border-t-0 md:border-l border-gray-100 pt-3 md:pt-0 md:pl-5">

                  {/* Currently View */}
                  {activeTab === 'currently' && (
                    <div className="flex flex-col gap-3">
                      <a
                        href={interview.meetingLink}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm font-medium"
                      >
                        <Video className="w-4 h-4" />
                        Join Interview
                      </a>
                    </div>
                  )}

                  {/* Upcoming View */}
                  {activeTab === 'upcoming' && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <button className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-xs font-medium">
                          <RefreshCw className="w-3.5 h-3.5" />
                          Reschedule
                        </button>
                        <button className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-white border border-red-200 text-red-600 rounded-md hover:bg-red-50 transition-colors text-xs font-medium">
                          <X className="w-3.5 h-3.5" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Completed View */}
                  {activeTab === 'completed' && (
                    <div className="space-y-3">
                      <div className="flex items-start gap-2">
                        <MessageSquare className="w-4 h-4 text-secondary-400 mt-1 shrink-0" />
                        <p className="text-sm text-secondary-600 italic">"{interview.feedback}"</p>
                      </div>

                      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                        {interview.outcome === 'rejected' ? (
                          <div className="flex items-center gap-1.5 text-red-600">
                            <XCircle className="w-4 h-4" />
                            <span className="font-semibold text-sm">Rejected</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 text-green-600">
                            <CheckCircle className="w-4 h-4" />
                            <div className="flex flex-col">
                              <span className="font-semibold text-sm">Pipeline</span>
                              <span className="text-xs text-green-700 font-medium">Schedule: {interview.time}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
            <Calendar className="w-10 h-10 text-secondary-300 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-secondary-900">No interviews found</h3>
            <p className="text-secondary-500">No interviews currently in this tab.</p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-gray-200 pt-4">
        <div className="text-sm text-gray-500">
          Showing <span className="font-medium">{filteredInterviews.length}</span> of <span className="font-medium">{stats.total}</span> results
        </div>
        <div className="flex items-center gap-2">
          <button className="px-3 py-1 border rounded-md text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-1">Prev</button>
          <button className="px-3 py-1 border rounded-md text-sm text-gray-600 hover:bg-gray-50">1</button>
          <button className="px-3 py-1 border rounded-md text-sm bg-primary-600 text-white border-primary-600">2</button>
          <button className="px-3 py-1 border rounded-md text-sm text-gray-600 hover:bg-gray-50">3</button>
          <button className="px-3 py-1 border rounded-md text-sm text-gray-600 hover:bg-gray-50">4</button>
          <button className="px-3 py-1 border rounded-md text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-1">Next</button>
        </div>
      </div>
    </div>
  )
}

export default CompanyInterviews
